/* eslint-disable react/prop-types */
import { useEffect, useMemo, useState } from "react";
import {Button, Col, Form, Modal, Row} from "react-bootstrap";
import i18n from "../../plugins/locale/i18n";
import loadDictionary from "../../plugins/locale/load-dictionary";
import PaymentOrderService from "../../service/payment-order-service";
import PartyService from "../../service/party-service";
import BankAccountService from "../../service/bank-account-service";
import Pasoonate from "pasoonate";
import NumberInput from "../components/NumberInput";

const paymentOrderService = new PaymentOrderService();
const partyService = new PartyService();
const bankAccountService = new BankAccountService();

await loadDictionary('payment-order-edit-window');

function EditPaymentOrderWindow({isOpenWindow, onCloseWindow, paymentOrderId}) {
  const [id, setId] = useState(0);
  const [partyId, setPartyId] = useState(0);
  const [partyBankAccountId, setPartyBankAccountId] = useState(0);
  const [amount, setAmount] = useState(0);
  const [dueDateYear, setDueDateYear] = useState(0);
  const [dueDateMonth, setDueDateMonth] = useState(0);
  const [dueDateDay, setDueDateDay] = useState(0);
  const [description, setDescription] = useState('');
  const [parties, setParties] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const bankAccountOptions = useMemo(() => {
    if(!partyId) {
      return [<option key={0} value={0}>{i18n.t('payment-order-edit-window.chooseParty')}</option>];
    }

    return bankAccounts.map((bankAccount) => (
      <option key={bankAccount.id} value={bankAccount.id}>{bankAccount.name}</option>
    ));
  }, [bankAccounts, partyId]);
  const dayOptions = useMemo(() => {
    const options = [];

    for(let i = 1; i <= 31; i++) {
      options.push(<option key={i} value={i}>{i}</option>);
    }

    return options;
  }, []);
  const monthOptions = useMemo(() => {
    const options = [];

    for(let i = 1; i <= 12; i++) {
      options.push(<option key={i} value={i}>{i}</option>);
    }

    return options;
  }, []);
  const yearOptions = useMemo(() => {
    const options = [];

    for(let i = 1404; i <= 1405; i++) {
      options.push(<option key={i} value={i}>{i}</option>);
    }

    return options;
  }, []);

  const handleClose = () => { 
    setId(0);
    setPartyId(0);
    setPartyBankAccountId(0);
    setAmount(0);
    setDueDateYear(0);
    setDueDateMonth(0);
    setDueDateDay(0);
    setDescription('');
    setBankAccounts([]);
    onCloseWindow();
  };
  
  const handleSubmit = () => {
    const dueDate = Pasoonate.make().jalali().setDate(dueDateYear, dueDateMonth, dueDateDay);


    const paymentOrder = {
      id: parseInt(id),
      party_id: parseInt(partyId),
      party_bank_account_id: parseInt(partyBankAccountId),
      amount: amount,
      due_date: dueDate.gregorian().format("yyyy-MM-dd"),
      description: description
    };

    paymentOrderService.edit(paymentOrder).then(result => {
      if(result) {
        handleClose();
      }
    }).catch(error => {
      console.error(error);
    })
  }

  useEffect(() => {
    if(isOpenWindow === false) return; 

    partyService.findAll().then(parties => {
      const options = parties.map((party) => <option key={party.id} value={party.id}>{party.name}</option>);
      options.unshift(<option key={0} value={0}>{i18n.t('global.chooseOption')}</option>);

      setParties(options);
    });
  }, [isOpenWindow]);

  useEffect(() => {
    if(!isOpenWindow || !paymentOrderId) return;
   
    paymentOrderService.findOne(paymentOrderId).then(paymentOrder => {
      const dueDate = Pasoonate.make().gregorian(paymentOrder.due_date).jalali().getDate();

      setId(paymentOrder.id);
      setPartyId(paymentOrder.party_id);
      setPartyBankAccountId(paymentOrder.party_bank_account_id);
      setAmount(paymentOrder.amount);
      setDueDateDay(dueDate.day);
      setDueDateMonth(dueDate.month);
      setDueDateYear(dueDate.year);
      setDescription(paymentOrder.description ?? ''); 
    });

  }, [isOpenWindow, paymentOrderId]);

  useEffect(() => {
    if(!isOpenWindow || !partyId) return;

    bankAccountService.findAllByPartyId(partyId).then(bankAccounts => {
      setBankAccounts(bankAccounts);
      setPartyBankAccountId(currentId => {
        const currentBankAccountExists = bankAccounts.some(bankAccount => String(bankAccount.id) === String(currentId));

        return currentBankAccountExists ? currentId : (bankAccounts[0]?.id ?? 0);
      });
    });
  }, [isOpenWindow, partyId]);

  return (
    <Modal show={isOpenWindow} onHide={handleClose} backdrop="static" keyboard={false}>
      <Modal.Header closeButton>
        <Modal.Title>{i18n.t("payment-order-edit-window.title")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form method="POST" id="CreateForm" onSubmit={e => {e.preventDefault(); handleSubmit()}}>
          <Form.Group className="mb-3" controlId="">
            <Form.Label>{i18n.t("payment-order-edit-window.party")}</Form.Label>
            <Form.Select autoFocus value={partyId} onChange={e => {
              setPartyId(parseInt(e.target.value));
              setPartyBankAccountId(0);
              setBankAccounts([]);
            }}>
              {parties}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3" controlId="">
            <Form.Label>{i18n.t("payment-order-edit-window.party_bank_account")}</Form.Label>
            <Form.Select value={partyBankAccountId} onChange={e => {setPartyBankAccountId(e.target.value)}}>
              {bankAccountOptions}
            </Form.Select>
          </Form.Group>
          <NumberInput className="mb-3" label={i18n.t("payment-order-edit-window.amount")} value={amount} onChange={setAmount} />
          <Row className="mb-3">
            <Form.Group as={Col} controlId="">
              <Form.Label>{i18n.t("payment-order-edit-window.due_date_day")}</Form.Label>
              <Form.Select value={dueDateDay} onChange={e => {setDueDateDay(e.target.value)}}>
                {dayOptions}
              </Form.Select>
            </Form.Group>
            <Form.Group as={Col} controlId="">
              <Form.Label>{i18n.t("payment-order-edit-window.due_date_month")}</Form.Label>
              <Form.Select value={dueDateMonth} onChange={e => {setDueDateMonth(e.target.value)}}>
                {monthOptions}
              </Form.Select>
            </Form.Group>
            <Form.Group as={Col} controlId="">
              <Form.Label>{i18n.t("payment-order-edit-window.due_date_year")}</Form.Label>
              <Form.Select value={dueDateYear} onChange={e => {setDueDateYear(e.target.value)}}>
                {yearOptions}
              </Form.Select>
            </Form.Group>
          </Row>
          <Form.Group className="mb-3" controlId="">
            <Form.Label>{i18n.t("payment-order-edit-window.description")}</Form.Label>
            <Form.Control type="text" value={description} onChange={e => {setDescription(e.target.value)}} />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>{i18n.t("payment-order-edit-window.cancel_button")}</Button>
        <Button variant="primary" type="submit" form="CreateForm" >{i18n.t("payment-order-edit-window.edit_button")}</Button>
      </Modal.Footer>
    </Modal>
  );
}

export default EditPaymentOrderWindow;
